import { useEffect } from "react";

import { useImageryStackStore } from "../stores/useImageryStackStore";

const SOURCE_ID = "imagery-footprints";
const FILL_ID = "imagery-footprints-fill";
const OUTLINE_ID = "imagery-footprints-outline";
const OUTLINE_ACTIVE_ID = "imagery-footprints-outline-active";

// feature-state flag: the footprint's series is in the Active Layers panel.
// Set reactively (see the highlight effect below). Inactive (the default,
// undefined → false) reproduces today's look exactly.
const ACTIVE = ["boolean", ["feature-state", "active"], false];

function ensureFootprints(map, url, addLayer) {
  if (!map) return;

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, {
      type: "geojson",
      data: url,
      promoteId: "dataset_id",
    });
  }

  // Fill — OFF when inactive (opacity 0.0, exactly as before this layer was
  // re-enabled); a light blue wash only when the series is active. Start light
  // (0.10) since it overlays live imagery — tune in-browser; drop to 0.0 for
  // outline-only if it muddies the imagery.
  addLayer({
    group: "footprint",
    layerConfig: {
      id: FILL_ID,
      type: "fill",
      source: SOURCE_ID,
      paint: {
        "fill-color": "#1f9dff",
        "fill-opacity": ["case", ACTIVE, 0.1, 0.0],
      },
    },
  });

  // Inactive outline: dashed pale-yellow (unchanged from today). Hidden when
  // active — line-dasharray can't be feature-state-driven, so the active "solid"
  // look is the separate layer below rather than a dash toggle here. Inactive
  // branch === today (color, width, opacity, dash).
  addLayer({
    group: "footprint",
    layerConfig: {
      id: OUTLINE_ID,
      type: "line",
      source: SOURCE_ID,
      paint: {
        "line-color": "#d1d361",
        "line-width": 2.0,
        "line-opacity": ["case", ACTIVE, 0.0, 0.9],
        "line-dasharray": [0.5, 0.5],
      },
    },
  });

  // Active outline: solid blue, visible ONLY when active (opacity 0 otherwise).
  // No dash → the "drop the dash" solid look. Same source, same width/opacity as
  // the inactive outline.
  addLayer({
    group: "footprint",
    layerConfig: {
      id: OUTLINE_ACTIVE_ID,
      type: "line",
      source: SOURCE_ID,
      paint: {
        "line-color": "#1f9dff",
        "line-width": 2.0,
        "line-opacity": ["case", ACTIVE, 0.9, 0.0],
      },
    },
  });
}

export function useSyncFootprintLayers({ map, footprintsUrl, addLayer }) {
  // Active-layers membership (the stack), the imagery catalog (every dataset_id
  // grouped by series), and the time cursor — a follow-cursor layer resolves to
  // a different acquisition as the cursor moves, so its highlighted footprint
  // must track that. All three live in one store.
  const stack = useImageryStackStore((s) => s.stack);
  const catalog = useImageryStackStore((s) => s.catalog);
  const cursor = useImageryStackStore((s) => s.cursor);

  // Mount the source + layers once.
  useEffect(() => {
    if (!map || !footprintsUrl) return;
    ensureFootprints(map, footprintsUrl, addLayer);
  }, [map, footprintsUrl, addLayer]);

  // Reactive highlight. ACQUISITION-LEVEL (per active layer, not per series):
  // a footprint is "active" iff its acquisition is the one a stack entry
  // currently resolves to. We reuse the store's resolver (getRenderSpecs →
  // resolveImageryStack), which already honors pinned vs follow-cursor and
  // degraded entries, then map each resolved (series_key, effective_date) to its
  // dataset_id via the catalog. Re-runs on stack / cursor / catalog changes.
  // (Coverage is sparse multi-polys today, so per-layer reads truer than
  // per-series; revisit if series gain dense coverage.)
  useEffect(() => {
    if (!map) return;

    const apply = () => {
      // setFeatureState needs the source loaded. Removing a layer churns the
      // imagery sources (removeSource via useSyncRegistryLayers), which makes
      // isSourceLoaded() report false transiently. The static footprints source
      // won't re-fire `sourcedata` on its own, so when it's not ready we retry
      // on `idle` (fires once the map settles) — otherwise the active:false
      // write is dropped and a removed layer keeps its blue highlight.
      if (!map.getSource(SOURCE_ID) || !map.isSourceLoaded(SOURCE_ID)) {
        map.once("idle", apply);
        return;
      }

      // The dataset_id each active layer resolves to right now.
      const specs = useImageryStackStore.getState().getRenderSpecs();
      const seriesByKey = new Map(
        (catalog?.series ?? []).map((s) => [s.series_key, s])
      );
      const activeDatasetIds = new Set();
      for (const spec of specs) {
        if (spec.degraded || !spec.effective_date) continue;
        const series = seriesByKey.get(spec.series_key);
        const acq = series?.acquisitions?.find(
          (a) => a.acquisition_date === spec.effective_date
        );
        if (acq?.dataset_id) activeDatasetIds.add(acq.dataset_id);
      }

      // Write active/inactive across every footprint in the catalog so removed
      // (or cursor-moved-off) layers reliably fall back to the dashed default.
      for (const series of catalog?.series ?? []) {
        for (const acq of series.acquisitions ?? []) {
          if (!acq?.dataset_id) continue;
          map.setFeatureState(
            { source: SOURCE_ID, id: acq.dataset_id },
            { active: activeDatasetIds.has(acq.dataset_id) }
          );
        }
      }
    };

    apply();

    return () => map.off("idle", apply);
  }, [map, stack, catalog, cursor]);
}
